import { useState, useCallback } from 'react';
import { storeFile } from './fileDatabase';
import mixpanel from 'mixpanel-browser';
import { BillingSource, UserTeamSummary } from '../../types';

interface UserInfo {
  name: string;
  email: string;
  status: string;
  credits_used: number;
  subscription_type?: string;
  // Team-aware credit fields from /auth/credit-info (see src/types.ts User).
  credits_total?: number | null;
  billing_source?: BillingSource;
  team?: UserTeamSummary | null;
  raw_status?: string;
  has_team?: boolean;
}

interface UseFileUploadProps {
  selectedModel: string;
  currentConversationId: number | null;
  userInfo: UserInfo | null;
  onFileSelected: (file: File, filename: string) => void;
  onFilesSelected: (files: { file: File; filename: string }[]) => void;
  onFilesAdded: (files: { file: File; filename: string }[]) => void;
  selectedFiles: { file: File; filename: string }[];
}

export const useFileUpload = ({
  selectedModel,
  currentConversationId,
  userInfo,
  onFileSelected,
  onFilesSelected,
  onFilesAdded,
  selectedFiles
}: UseFileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);

  // Helper function to check if file upload is enabled for the current model
  const isFileUploadEnabled = useCallback((model: string): boolean => {
    // 'auto' routing supports uploads - the backend picks a file/vision-capable model.
    if (model === 'auto') {
      return true;
    }

    // Check if it's a Claude model - all Claude models support file upload
    if (model.toLowerCase().includes('claude')) {
      return true;
    }
    
    // Check other specific models
    return [
      'gpt-4o',
      'gpt-4.1-2025-04-14',
      'gpt-4.1-mini-2025-04-14',
      'gpt-4.1-nano-2025-04-14',
      'gpt-5.2',
      'gpt-5-mini',
      'power-ai-chosen-model',
      'o1',
      'o1-mini',
      'o3-2025-04-16',
      'o4-mini-2025-04-16',
      'claude-3-5-haiku-latest',
      'claude-3-7-sonnet-latest',
      'claude-3-7-sonnet-thinking',
      'claude-3-5-sonnet-search',
      'claude-3-7-sonnet-thinking-search-latest',
      'gemini-2.0-flash-lite',
      'gemini-2.0-pro-exp-02-05',
      'gemini-2.0-flash-001',
      'gemini-2.0-flash-thinking-exp-01-21',
      'gemini-2.5-pro-exp-03-25',
      'google/gemini-2.5-pro-preview',
      'google/gemini-3.1-pro-preview',
      'google/gemini-2.5-flash-preview',
      'google/gemini-3-flash-preview',
      'google/gemini-2.5-flash-preview:thinking',
      'google/gemini-2.5-pro-exp-03-25:free',
      'grok-2-1212',
      'x-ai/grok-4.1-fast',
      'meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo',
      'meta-llama/Llama-3.3-70B-Instruct-Turbo',
      'meta-llama/Llama-3.3-70B-Instruct-Turbo-Free',
      'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
      'meta-llama/llama-4-maverick',
      'meta-llama/llama-4-scout-17b-16e-instruct',
      'gpt-image-1',
      'black-forest-labs/FLUX.1-kontext-max',
      'powerai-image-1',
      'openrouter/horizon-beta',
      'z-ai/glm-4.7',
      // 2026 catalog refresh
      'gpt-5.4',
      'gpt-5.4-mini',
      'gpt-5.5',
      'claude-haiku-4-5-20251001',
      'claude-sonnet-4-6',
      'claude-4-6-sonnet-thinking',
      'claude-opus-4-6',
      'claude-4-6-opus-thinking',
      'claude-opus-4-7',
      'claude-4-7-opus-thinking',
      'grok-4.20-0309-reasoning',
      'grok-4-fast-reasoning',
      'deepseek/deepseek-v4-pro',
      'deepseek/deepseek-v4-flash',
      'moonshotai/kimi-k2.6',
      'z-ai/glm-5.1',
      'qwen/qwen3.5-plus-02-15',
      'mistralai/mistral-medium-3.1',
      'gpt-image-2',
      'google/gemini-3.1-flash-image-preview',
      'black-forest-labs/FLUX.2-pro'
    ].includes(model);
  }, []);

  // Common file processing logic
  const processFile = useCallback(async (
    file: File, 
    uploadMethod: 'file_input' | 'drag_and_drop' | 'clipboard_paste',
    filename?: string
  ) => {
    const timestamp = Date.now();
    const finalFilename = filename || file.name;
    const timestampedFilename = `${timestamp}-${finalFilename}`;
    
    // Store in IndexedDB
    await storeFile(timestampedFilename, file);
    
    // Call the callback to update parent component state
    onFileSelected(file, timestampedFilename);
    
    // Track file upload
    mixpanel.track('File Uploaded', {
      file_type: file.type,
      file_size: file.size,
      file_name: finalFilename,
      upload_method: uploadMethod,
      model: selectedModel,
      conversation_id: currentConversationId,
      user_email: userInfo?.email,
      user_status: userInfo?.status
    });
    
    // Increment file upload count
    mixpanel.people.increment('Total Files Uploaded');
  }, [selectedModel, currentConversationId, userInfo, onFileSelected]);

  // File input handler
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isFileUploadEnabled(selectedModel)) {
      alert('File upload is only available for GPT-4o, Claude 3.5, and Gemini models');
      return;
    }

    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const processedFiles: { file: File; filename: string }[] = [];
      
      // Check each file individually
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 10 * 1024 * 1024) {
          alert(`File "${file.name}" size should not exceed 10MB`);
          
          // Track failed upload due to size
          mixpanel.track('File Upload Failed', {
            reason: 'size_limit_exceeded',
            file_size: file.size,
            file_type: file.type,
            file_name: file.name,
            model: selectedModel,
            user_email: userInfo?.email
          });
          
          continue; // Skip this file but continue with others
        }
        
        // Use timestamp + index to ensure unique filenames
        const timestamp = Date.now() + i;
        const timestampedFilename = `${timestamp}-${file.name}`;
        
        // Store in IndexedDB
        await storeFile(timestampedFilename, file);
        
        processedFiles.push({ file, filename: timestampedFilename });
        
        // Track file upload
        mixpanel.track('File Uploaded', {
          file_type: file.type,
          file_size: file.size,
          file_name: file.name,
          upload_method: 'file_input',
          model: selectedModel,
          conversation_id: currentConversationId,
          user_email: userInfo?.email,
          user_status: userInfo?.status
        });
      }
      
      if (processedFiles.length > 0) {
        // Add new files to existing files instead of replacing
        onFilesAdded(processedFiles);
        
        // Increment file upload count
        mixpanel.people.increment('Total Files Uploaded', processedFiles.length);
      }
    }
  }, [selectedModel, userInfo, isFileUploadEnabled, currentConversationId, onFilesAdded]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (!isFileUploadEnabled(selectedModel)) {
      alert('File upload is only available for GPT-4o, Claude 3.5, and Gemini models');
      return;
    }
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const processedFiles: { file: File; filename: string }[] = [];
      
      // Check each file individually
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 10 * 1024 * 1024) {
          alert(`File "${file.name}" size should not exceed 10MB`);
          
          // Track failed upload due to size
          mixpanel.track('File Upload Failed', {
            reason: 'size_limit_exceeded',
            file_size: file.size,
            file_type: file.type,
            file_name: file.name,
            model: selectedModel,
            user_email: userInfo?.email
          });
          
          continue; // Skip this file but continue with others
        }
        
        // Use timestamp + index to ensure unique filenames
        const timestamp = Date.now() + i;
        const timestampedFilename = `${timestamp}-${file.name}`;
        
        // Store in IndexedDB
        await storeFile(timestampedFilename, file);
        
        processedFiles.push({ file, filename: timestampedFilename });
        
        // Track file upload
        mixpanel.track('File Uploaded', {
          file_type: file.type,
          file_size: file.size,
          file_name: file.name,
          upload_method: 'drag_and_drop',
          model: selectedModel,
          conversation_id: currentConversationId,
          user_email: userInfo?.email,
          user_status: userInfo?.status
        });
      }
      
      if (processedFiles.length > 0) {
        // Add new files to existing files instead of replacing
        onFilesAdded(processedFiles);
        
        // Increment file upload count
        mixpanel.people.increment('Total Files Uploaded', processedFiles.length);
      }
    }
  }, [selectedModel, userInfo, isFileUploadEnabled, currentConversationId, onFilesAdded]);

  // Clipboard paste handler
  const handleClipboardPaste = useCallback(async (e: ClipboardEvent) => {
    // Check if file upload is enabled for the current model
    if (!isFileUploadEnabled(selectedModel)) {
      return;
    }

    const items = e.clipboardData?.items;
    if (!items) return;

    // Look for image items in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if item is an image
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault(); // Prevent default paste behavior
        
        const file = item.getAsFile();
        if (file) {
          // Check file size (10MB limit)
          if (file.size > 10 * 1024 * 1024) {
            alert('Image size should not exceed 10MB');
            
            // Track failed upload due to size
            mixpanel.track('File Upload Failed', {
              reason: 'size_limit_exceeded',
              file_size: file.size,
              file_type: file.type,
              model: selectedModel,
              upload_method: 'clipboard_paste',
              user_email: userInfo?.email
            });
            
            return;
          }

          // Generate filename with timestamp
          const timestamp = Date.now();
          const extension = file.type.split('/')[1] || 'png';
          const filename = `pasted-image-${timestamp}.${extension}`;

          try {
            await processFile(file, 'clipboard_paste', filename);
            
            // Show success feedback (optional)
            console.log('Image pasted successfully:', filename);
            
          } catch (error) {
            console.error('Error storing pasted image:', error);
            alert('Failed to process pasted image. Please try again.');
          }
        }
        break; // Only handle the first image found
      }
    }
  }, [selectedModel, userInfo, isFileUploadEnabled, processFile]);

  return {
    isDragging,
    isFileUploadEnabled: (model: string) => isFileUploadEnabled(model),
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClipboardPaste
  };
};